import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import { Partner, SearchResult, searchAccounts, getPartnerAnalytics } from '../api/partner';

export const AccountSearchPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);

  const fetchPartners = async () => {
    try {
      setIsAnalyticsLoading(true);
      const data = await getPartnerAnalytics();
      setPartners(data);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      setResults([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const data = await searchAccounts(query);
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setError('Failed to search accounts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const formatNotes = (notes: string) => {
    return notes.split('\n').map((note, index) => (
      <div key={index}>{note}</div>
    ));
  };

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      gap: 3,
      minHeight: '100vh',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Partner Analytics
        </Typography>

        <Paper sx={{ 
          p: 3, 
          width: '100%',
          minWidth: 'fit-content'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" color="textSecondary">
              Last refreshed: {lastRefreshed.toLocaleString()}
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchPartners}
              disabled={isAnalyticsLoading}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer sx={{ width: '100%' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner Name</TableCell>
                  <TableCell align="right">Account Count</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>{partner.name}</TableCell>
                    <TableCell align="right">{partner.account_count}</TableCell>
                    <TableCell>
                      {new Date(partner.last_updated || partner.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {partners.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {isAnalyticsLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        'No partners found'
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box>
        <Typography variant="h4" gutterBottom>
          Account Search
        </Typography>
        
        <Paper sx={{ 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: '500px',
          minWidth: 'fit-content'
        }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
          <TextField
            fullWidth
            label="Search Account Name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter account name..."
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !query.trim()}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            Search
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer sx={{ 
          mt: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          width: '100%',
          overflowX: 'auto'
        }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: '1000px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '25%' }}>Account Name</TableCell>
                <TableCell sx={{ width: '15%' }}>Partner</TableCell>
                <TableCell sx={{ width: '60%' }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result, index) => (
                <TableRow 
                  key={`${result.partner_name}-${result.account_name}-${index}`}
                  sx={{
                    backgroundColor: theme => {
                      switch (result.match_score) {
                        case 3: return theme.palette.success.light;
                        case 2: return theme.palette.info.light;
                        case 1: return theme.palette.warning.light;
                        default: return 'inherit';
                      }
                    },
                    '&:hover': {
                      backgroundColor: theme => theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell>{result.account_name}</TableCell>
                  <TableCell>{result.partner_name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>{formatNotes(result.notes || '')}</TableCell>
                </TableRow>
              ))}
              {results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ height: '200px' }}>
                    {isLoading ? (
                      <CircularProgress />
                    ) : query ? (
                      `No accounts found matching "${query}"`
                    ) : (
                      'Enter an account name to search'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
};
